// React
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';

// Redux
import { useAppSelector } from '../../../redux/hooks';

// MUI Components
import { Autocomplete, Badge, Box, Button, Card, Checkbox, Chip, CircularProgress, ClickAwayListener, Fade, FormControl, Grid, Icon, IconButton, Paper, Popper, PopperPlacementType, TextField, Tooltip, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// @MUI Icons
import WorkIcon from '@mui/icons-material/Work';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

// Types
import { Site } from '../../../@types';

// Utils
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { useTranslation } from 'react-i18next';
import { newAdapter } from "../../../utils";
import dayjs, { Dayjs } from 'dayjs';

// Services
import { getJobsDashboardData } from '../../../services/DashboardService';
// import { getAllSites } from '../../../services/SiteService'; //เปลี่ยนมาใช้ useAppSelector

const JobDashboard = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    // เปลี่ยนมาใช้ useAppSelector
    // const [siteData, setSiteData] = useState<Site[]>([]);
    const { sites } = useAppSelector((state) => state.masterData);
    const siteData = useMemo(
        () => [...sites].sort((a: Site, b: Site) => a.site_id.localeCompare(b.site_id)),
        [sites]
    );

    const [jobDashboardData, setJobDashboardData] = useState<any>(null);
    const [selectedSites, setSelectedSites] = useState<Site[]>([]);
    const [loadingSites, setLoadingSites] = useState<boolean>(false); // เปลี่ยนมาใช้ useAppSelector (ข้อมูลพร้อมใช้จาก store แล้ว)
    const [loadingJobDashboard, setLoadingJobDashboard] = useState<boolean>(true);
    const [jobCreatedDateRange, setJobCreatedDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [countFilterJobDashboardSelected, setCountFilterJobDashboardSelected] = useState<number>(0);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [placement, setPlacement] = useState<PopperPlacementType>();
    const [filterJobDashboardOpen, setFilterJobDashboardOpen] = useState<boolean>(false);

    const handleFilterJobDashboardOpenClick =
        (newPlacement: PopperPlacementType) =>
            (event: React.MouseEvent<HTMLButtonElement>) => {
                setAnchorEl(event.currentTarget);
                setFilterJobDashboardOpen((prev) => placement !== newPlacement || !prev);
                setPlacement(newPlacement);
            };

    useEffect(() => {
        const fetchJobDashboard = async () => {
            setLoadingJobDashboard(true);

            const response = await getJobsDashboardData({
                site_ids: selectedSites?.map((site: Site) => site.site_id).length !== 0 ? selectedSites?.map((site: Site) => site.site_id) : undefined,
                created_date_range: jobCreatedDateRange[0] || jobCreatedDateRange[1] ? jobCreatedDateRange : undefined,
            });

            if (response.data?.success) {
                setLoadingJobDashboard(false);
                setJobDashboardData(response.data?.data);
            }
        };

        fetchJobDashboard();
    }, [selectedSites, jobCreatedDateRange]);

    // เปลี่ยนมาใช้ useAppSelector
    // useEffect(() => {
    //     const fetchSites = async () => {
    //         try {
    //             const response = await getAllSites();

    //             if (response.data?.success) {
    //                 setSiteData(response.data?.sites);
    //             }
    //         } catch (error) {
    //             navigate('/servererror', { replace: true });
    //         }
    //     };

    //     const siteSorting = async () => {
    //         try {
    //             siteData.sort((a, b) => a.site_id.localeCompare(b.site_id));
    //             setTimeout(() => {
    //                 setLoadingSites(false);
    //             }, 500);
    //         } catch (error) {
    //             navigate('/servererror', { replace: true });
    //         }
    //     }

    //     fetchSites();
    //     siteSorting();

    // }, []);

    useEffect(() => {
        let count = 0;
        if (selectedSites && selectedSites.length > 0) count++;
        if (jobCreatedDateRange[0] || jobCreatedDateRange[1]) count++;
        setCountFilterJobDashboardSelected(count);
    }, [selectedSites, jobCreatedDateRange])

    const handleClickAwayJobDashboardFilter = () => {
        setFilterJobDashboardOpen(false);
    };

    const styleCard = {
        borderRadius: '0.5rem', height: '100%', p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
    };

    const shortcutsPreviousDateItems: { label: string; getValue: () => [Dayjs, Dayjs] }[] = [
        {
            label: `${t('Today')}`,
            getValue: () => {
                const start_today = dayjs().startOf('day');
                const end_today = dayjs().endOf('day');
                return [start_today, end_today];
            },
        },
        {
            label: `${t('Yesterday')}`,
            getValue: () => {
                const today = dayjs();
                const yesterday = today.subtract(1, 'day');
                return [yesterday.startOf('day'), yesterday.endOf('day')];
            },
        },
        {
            label: `${t('This Week')}`,
            getValue: () => {
                const today = dayjs();
                return [today.startOf('week'), today.endOf('week')];
            },
        },
        {
            label: `${t('Last Week')}`,
            getValue: () => {
                const today = dayjs();
                const prevWeek = today.subtract(7, 'day');
                return [prevWeek.startOf('week'), prevWeek.endOf('week')];
            },
        },
        {
            label: `${t('Last 7 Days')}`,
            getValue: () => {
                const start_today = dayjs().startOf('day');
                const end_today = dayjs().endOf('day');
                const prevWeek = start_today.subtract(7, 'day');
                return [prevWeek, end_today];
            },
        },
        {
            label: `${t('Last 30 Days')}`,
            getValue: () => {
                const start_today = dayjs().startOf('day');
                const end_today = dayjs().endOf('day');
                const prevMonth = start_today.subtract(30, 'day');
                return [prevMonth, end_today];
            },
        },
        {
            label: `${t('This Month')}`,
            getValue: () => {
                const today = dayjs();
                return [today.startOf('month'), today.endOf('month')];
            },
        },
        {
            label: `${t('Last Month')}`,
            getValue: () => {
                const today = dayjs();
                const prevMonth = today.subtract(1, 'month');
                return [prevMonth.startOf('month'), prevMonth.endOf('month')];
            },
        },
    ];

    return (
        <div>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                    {t("Information of job positions")}
                </Typography>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { sm: 'fit-content(100%) 2fr' },
                    gridTemplateRows: { xs: 'fit-content(100%) repeat(1, 1fr)' },
                }}>
                    <Box sx={{ gridRow: { xs: 2, sm: 1 }, gridColumn: { sm: 3 } }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '50% 50%', sm: 'repeat(1, 1fr)' } }}>
                            <Box sx={{
                                py: 1, display: 'flex', alignItems: 'center', justifyContent: { sm: 'flex-end' }, '&:hover': {
                                    cursor: 'pointer',
                                }, gridColumn: 1
                            }}>
                                <Button onClick={handleFilterJobDashboardOpenClick('bottom-end')}>
                                    <Badge badgeContent={countFilterJobDashboardSelected} color="primary">
                                        <Icon >filter_list</Icon>
                                    </Badge>
                                    <Typography sx={{ userSelect: 'none' }}>
                                        {t("Filter")}
                                    </Typography>
                                </Button>

                                <Popper open={filterJobDashboardOpen} anchorEl={anchorEl} placement={placement} transition>
                                    {({ TransitionProps }) => (
                                        <ClickAwayListener onClickAway={handleClickAwayJobDashboardFilter}>
                                            <Fade {...TransitionProps} timeout={50}>
                                                <Paper sx={{ width: '400px', display: 'flex', flexDirection: 'column', py: 1, px: 3, border: '1px solid #e0e0e0' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                                        <LocalizationProvider dateAdapter={newAdapter} adapterLocale={i18n.language === 'en' ? 'en' : 'th'}>
                                                            {/* MobileDateRangePicker (Pro) ถูกแทนด้วย DatePicker ฟรี 2 ตัว + ปุ่ม shortcut เอง */}
                                                            <Box sx={{ mt: 1 }}>
                                                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                                                    {t("FilterByJobCreatedDate")}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                                    <DatePicker
                                                                        label={t('StartDateRange')}
                                                                        value={jobCreatedDateRange[0]}
                                                                        onChange={(date: any) => {
                                                                            setJobCreatedDateRange([
                                                                                date ? dayjs(date).startOf('day') : null,
                                                                                jobCreatedDateRange[1],
                                                                            ]);
                                                                        }}
                                                                        format="DD/MM/YYYY"
                                                                        slotProps={{ textField: { size: 'small' } }}
                                                                    />
                                                                    <Typography>-</Typography>
                                                                    <DatePicker
                                                                        label={t('EndDateRange')}
                                                                        value={jobCreatedDateRange[1]}
                                                                        minDate={jobCreatedDateRange[0] ?? undefined}
                                                                        onChange={(date: any) => {
                                                                            setJobCreatedDateRange([
                                                                                jobCreatedDateRange[0],
                                                                                date ? dayjs(date).endOf('day') : null,
                                                                            ]);
                                                                        }}
                                                                        format="DD/MM/YYYY"
                                                                        slotProps={{ textField: { size: 'small' } }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                                    {shortcutsPreviousDateItems.map((item) => (
                                                                        <Chip
                                                                            key={item.label}
                                                                            label={item.label}
                                                                            size="small"
                                                                            clickable
                                                                            onClick={() => {
                                                                                const [start, end] = item.getValue();
                                                                                setJobCreatedDateRange([start, end]);
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        </LocalizationProvider>
                                                        <Tooltip title={t("Clear")}>
                                                            <IconButton
                                                                aria-label="delete"
                                                                size="small"
                                                                sx={{ height: 'fit-content', ml: 1 }}
                                                                onClick={() => {
                                                                    setJobCreatedDateRange([null, null]);
                                                                }}
                                                            >
                                                                <Icon>clear</Icon>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                                        <Box sx={{ width: '100%' }}>
                                                            <Typography sx={{ mt: 2, mb: 1, fontSize: '0.875rem' }}>{t("FilterBySite")}</Typography>
                                                            <FormControl sx={{ width: '100%' }}>
                                                                <Autocomplete
                                                                    onChange={(event, newValue: Site[]) => {
                                                                        setSelectedSites(newValue);
                                                                    }}
                                                                    multiple
                                                                    disableCloseOnSelect
                                                                    limitTags={1}
                                                                    id="site"
                                                                    options={
                                                                        siteData
                                                                    }
                                                                    size="small"
                                                                    loading={loadingSites}
                                                                    loadingText={`${t("Loading")}...`}
                                                                    getOptionLabel={(option) => option.site_name}
                                                                    value={selectedSites}
                                                                    renderOption={(props, option, { selected }) => (
                                                                        <li {...props}>
                                                                            <Checkbox
                                                                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                                                style={{ marginRight: 8 }}
                                                                                checked={selected}
                                                                            />
                                                                            {option.site_name}
                                                                        </li>
                                                                    )}
                                                                    renderInput={(params) => (
                                                                        <TextField {...params}
                                                                            label={loadingSites ? `${t("Loading")}...` : t("Site")}
                                                                            placeholder={t("Site")!}
                                                                            InputProps={{
                                                                                ...params.InputProps,
                                                                                endAdornment: (
                                                                                    <React.Fragment>
                                                                                        {loadingSites ? <CircularProgress color="inherit" size={20} /> : null}
                                                                                        {params.InputProps.endAdornment}
                                                                                    </React.Fragment>
                                                                                ),
                                                                            }} />
                                                                    )}
                                                                    renderTags={(value: any, getTagProps) =>
                                                                        value.map((option: Site, index: number) => (
                                                                            <Chip
                                                                                variant="outlined"
                                                                                label={t(option.site_name)}
                                                                                {...getTagProps({ index })}
                                                                            />
                                                                        ))
                                                                    }
                                                                    noOptionsText={t("No options found")}
                                                                />
                                                            </FormControl>
                                                        </Box>
                                                        <Tooltip title={t("Clear")}>
                                                            <IconButton
                                                                aria-label="delete"
                                                                size="small"
                                                                sx={{ height: 'fit-content', ml: 1 }}
                                                                onClick={() => {
                                                                    setSelectedSites([]);
                                                                }}
                                                            >
                                                                <Icon>clear</Icon>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                                        <Button
                                                            sx={{
                                                                fontSize: 13,
                                                            }}
                                                            onClick={() => {
                                                                setJobCreatedDateRange([null, null]);
                                                                setSelectedSites([]);
                                                                setFilterJobDashboardOpen(false);
                                                            }}
                                                        >
                                                            {t('RemoveAllFilters')}
                                                        </Button>
                                                    </Box>
                                                </Paper>
                                            </Fade>
                                        </ClickAwayListener>
                                    )}
                                </Popper>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={6} md={6} lg={3} xl={2}>
                    <Card variant="outlined" sx={styleCard}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <WorkIcon sx={{ color: '#42A5F5', fontSize: '2rem', backgroundColor: '#E3F2FD', borderRadius: '50%', p: 1 }} />
                            <Typography sx={{ fontSize: '14px', opacity: '0.8' }}>
                                {t('All job positions created')}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                            <Typography sx={{ fontSize: 40, fontWeight: 'bold' }}>{loadingJobDashboard ? '...' : jobDashboardData?.create_job_count}</Typography>
                            <Typography sx={{ fontSize: '16px' }} gutterBottom>
                                {t('positions')}
                            </Typography>
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={6} md={6} lg={3} xl={2}>
                    <Card variant="outlined" sx={styleCard}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <WorkIcon sx={{ color: '#027007', fontSize: '2rem', backgroundColor: '#e4f2e4', borderRadius: '50%', p: 1 }} />
                            <Typography sx={{ fontSize: '14px', opacity: '0.8' }}>
                                {t('All job positions opened')}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                            <Typography sx={{ fontSize: 40, fontWeight: 'bold' }}>{loadingJobDashboard ? '...' : jobDashboardData?.open_job_count}</Typography>
                            <Typography sx={{ fontSize: '16px' }} gutterBottom>
                                {t('positions')}
                            </Typography>
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={6} md={6} lg={3} xl={2}>
                    <Card variant="outlined" sx={styleCard}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <WorkIcon sx={{ color: '#FF4F40', fontSize: '2rem', backgroundColor: '#f5eeed', borderRadius: '50%', p: 1 }} />
                            <Typography sx={{ fontSize: '14px', opacity: '0.8' }}>
                                 {t('All job positions closed')}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                            <Typography sx={{ fontSize: 40, fontWeight: 'bold' }}>{loadingJobDashboard ? '...' : jobDashboardData?.close_job_count}</Typography>
                            <Typography sx={{ fontSize: '16px' }} gutterBottom>
                                {t('positions')}
                            </Typography>
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </div>
    )
}

export default JobDashboard